/**
 * Modal Context Configurations
 * 
 * Defines different contexts for the UnifiedResultsModal:
 * - Spots: Enhanced search for case-related jobs
 * - Similar Jobs: Content similarity matching
 * - Cross-Case: Multi-case investigation patterns
 * - Timeline: Temporal analysis of related jobs
 */

import EnhancedSpotsSearchProvider from '../searchProviders/EnhancedSpotsSearchProvider';
// Import other providers as they're created
// import ContentSimilaritySearchProvider from '../searchProviders/ContentSimilaritySearchProvider';
// import CrossCaseSearchProvider from '../searchProviders/CrossCaseSearchProvider';

// Context configurations
export const MODAL_CONTEXTS = {
  spots: {
    type: 'spots',
    title: 'Other Jobs',
    subtitle: 'Find jobs that may relate to evidence in this case',
    searchProvider: EnhancedSpotsSearchProvider,
    features: {
      statistics: true,
      filtering: true,
      sorting: true,
      viewModes: ['grid', 'list']
    },
    defaultViewMode: 'grid',
    resultActions: ['view', 'bookmark'],
    helpText: 'Use this to discover jobs from other investigators that analyzed your evidence files or found similar content (vehicles, plates, people).'
  },

  similarJobs: {
    type: 'similar-jobs',
    title: 'Similar Jobs',
    subtitle: 'Jobs with similar analysis results',
    // searchProvider: ContentSimilaritySearchProvider, // TODO: Create this
    features: {
      statistics: true,
      filtering: true,
      sorting: true,
      viewModes: ['grid', 'list']
    },
    defaultViewMode: 'list',
    resultActions: ['view', 'compare'],
    helpText: 'Find jobs that discovered similar content patterns (same vehicles, license plates, people) in your analysis results.'
  },

  crossCase: {
    type: 'cross-case',
    title: 'Cross-Case Analysis',
    subtitle: 'Investigation patterns across multiple cases',
    // searchProvider: CrossCaseSearchProvider, // TODO: Create this
    features: {
      statistics: true,
      filtering: true,
      sorting: true,
      viewModes: ['grid', 'list', 'timeline']
    },
    defaultViewMode: 'timeline',
    resultActions: ['view', 'analyze', 'export'],
    helpText: 'Analyze patterns and connections across multiple cases to identify broader investigation trends.'
  },

  timeline: {
    type: 'timeline',
    title: 'Timeline Analysis',
    subtitle: 'Temporal analysis of related jobs',
    // searchProvider: TimelineSearchProvider, // TODO: Create this
    features: {
      statistics: true,
      filtering: true,
      sorting: true,
      viewModes: ['timeline', 'list']
    },
    defaultViewMode: 'timeline',
    resultActions: ['view', 'analyze'],
    helpText: 'View related jobs in chronological order to understand the timeline of investigations.'
  }
};

/**
 * Get context configuration by type
 * @param {string} contextType - Type of context to get
 * @returns {Object} Context configuration
 */
export const getContextConfig = (contextType) => {
  const config = MODAL_CONTEXTS[contextType];
  if (!config) {
    throw new Error(`Unknown modal context: ${contextType}`);
  }
  return config;
};

/**
 * Create a context instance with data
 * @param {string} contextType - Type of context
 * @param {Object} data - Context-specific data
 * @param {Object} options - Additional options
 * @returns {Object} Context instance
 */
export const createContext = (contextType, data = {}, options = {}) => {
  const config = getContextConfig(contextType);
  
  return {
    ...config,
    data,
    options,
    // Create search provider instance
    searchProvider: config.searchProvider ? new config.searchProvider() : null,
    // Merge any custom result selection handler
    onResultSelect: options.onResultSelect || null
  };
};

/**
 * Validate context data
 * @param {string} contextType - Type of context
 * @param {Object} data - Data to validate
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateContextData = (contextType, data) => {
  getContextConfig(contextType); // Validate context exists
  
  switch (contextType) {
    case 'spots':
      return validateSpotsData(data);
    
    case 'similarJobs':
      return validateSimilarJobsData(data);
    
    default:
      return { isValid: true, errors: [] };
  }
};

// Context-specific validation functions
const validateSpotsData = (data) => {
  const errors = [];
  
  if (!data.casefileId) {
    errors.push('casefileId is required for Spots context');
  }
  
  if (!data.currentUserId) {
    errors.push('currentUserId is required for Spots context');
  }
  
  if (!data.evidenceOptions || !Array.isArray(data.evidenceOptions)) {
    errors.push('evidenceOptions array is required for Spots context');
  }
  
  if (!data.jobs || !Array.isArray(data.jobs)) {
    errors.push('jobs array is required for Spots context');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const validateSimilarJobsData = (data) => {
  const errors = [];
  
  if (!data.jobId) {
    errors.push('jobId is required for Similar Jobs context');
  }
  
  if (!data.currentResults) {
    errors.push('currentResults is required for Similar Jobs context');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const modalContextsExports = {
  MODAL_CONTEXTS,
  getContextConfig,
  createContext,
  validateContextData
};

export default modalContextsExports;
